/* Comforsyco Free Style Preview — native form (replaces the Shopify Forms app block).
 *
 * Standalone: shares the saved-styles store (localStorage `comforsyco_style_preview`,
 * owned by style-preview.js). The strip's × removes a single pick (mirroring the
 * site-wide bar); submitting never clears picks, so the customer can send again.
 *
 * Submits multipart/form-data to the endpoint set on the section
 * (data-endpoint → Cloudflare Worker → email to customer service).
 */
(() => {
  const STORE_KEY = 'comforsyco_style_preview';
  const MAX_PHOTOS = 3;
  const MAX_BYTES = 8 * 1024 * 1024;

  const readStyles = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORE_KEY) || '');
      if (!stored || !Array.isArray(stored.items)) return [];
      if (stored.expires_at && stored.expires_at < Date.now()) return [];
      return stored.items;
    } catch {
      return [];
    }
  };

  const pushEvent = (eventName, data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...data });
  };

  const initForm = (form) => {
    if (form.dataset.fspfReady) return;
    form.dataset.fspfReady = '1';

    const endpoint = form.dataset.endpoint || '';
    const drop = form.querySelector('[data-fspf-drop]');
    const fileInput = form.querySelector('[data-fspf-file]');
    const photoWrap = form.querySelector('[data-fspf-photos]');
    const errorBox = form.querySelector('[data-fspf-error]');
    const submitBtn = form.querySelector('[data-fspf-submit]');
    const successBox = form.parentElement.querySelector('[data-fspf-success]');

    let photos = [];

    // Saved styles strip: constant height with or without picks; each thumb has
    // an × that removes the pick from the shared store (same operation as the
    // site-wide bar; expiry is preserved, submitting still never clears picks).
    const savedThumbs = form.querySelector('[data-fspf-saved-thumbs]');
    const savedEmpty = form.querySelector('[data-fspf-saved-empty]');
    const savedNote = form.querySelector('[data-fspf-saved-note]');

    const removeStyle = (productId) => {
      try {
        const stored = JSON.parse(localStorage.getItem(STORE_KEY) || '');
        if (!stored || !Array.isArray(stored.items)) return;
        stored.items = stored.items.filter((item) => String(item.product_id) !== String(productId));
        localStorage.setItem(STORE_KEY, JSON.stringify(stored));
        pushEvent('style_preview_remove', { product_id: productId, page_url: window.location.href });
      } catch {
        /* noop */
      }
      renderSaved();
    };

    const renderSaved = () => {
      if (!savedThumbs) return;
      const items = readStyles().filter((item) => item.product_image);
      savedThumbs.querySelectorAll('span[data-fspf-thumb]').forEach((el) => el.remove());
      const hasItems = items.length > 0;
      if (savedEmpty) savedEmpty.hidden = hasItems;
      if (savedNote) savedNote.hidden = !hasItems;
      items.forEach((item) => {
        const cell = document.createElement('span');
        cell.dataset.fspfThumb = '1';
        cell.title = item.product_title || '';
        const img = document.createElement('img');
        img.src = item.product_image;
        img.alt = item.product_title || '';
        img.loading = 'lazy';
        const del = document.createElement('button');
        del.type = 'button';
        del.textContent = '×';
        del.setAttribute('aria-label', `Remove ${item.product_title || 'style'}`);
        del.addEventListener('click', () => removeStyle(item.product_id));
        cell.append(img, del);
        savedThumbs.append(cell);
      });
    };

    renderSaved();

    const showError = (text) => {
      errorBox.textContent = text || '';
      errorBox.hidden = !text;
    };

    const renderPhotos = () => {
      photoWrap.innerHTML = '';
      photos.forEach((file, index) => {
        const fig = document.createElement('figure');
        const img = document.createElement('img');
        img.src = URL.createObjectURL(file);
        img.onload = () => URL.revokeObjectURL(img.src);
        img.alt = '';
        const del = document.createElement('button');
        del.type = 'button';
        del.textContent = '×';
        del.setAttribute('aria-label', 'Remove photo');
        del.addEventListener('click', () => {
          photos.splice(index, 1);
          renderPhotos();
        });
        fig.append(img, del);
        photoWrap.append(fig);
      });
    };

    // Phone photos are 3–8 MB each; uploading originals made "Sending…" take
    // ~a minute on slow uplinks. Downscale to 1600px JPEG (~200–400 KB) before
    // upload — plenty for a hardware mock-up. Files that can't be decoded
    // client-side (e.g. HEIC in some browsers) are sent as-is.
    const MAX_DIM = 2000;
    const COMPRESS_OVER = 600 * 1024;
    const compressImage = async (file) => {
      if (file.size <= COMPRESS_OVER) return file;
      try {
        let bitmap;
        try {
          bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch {
          bitmap = await createImageBitmap(file);
        }
        const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
        if (!blob || blob.size >= file.size) return file;
        return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
      } catch {
        return file;
      }
    };

    const addFiles = async (list) => {
      showError('');
      for (const file of Array.from(list)) {
        if (photos.length >= MAX_PHOTOS) {
          showError(`Up to ${MAX_PHOTOS} photos, please.`);
          break;
        }
        if (!file.type.startsWith('image/')) {
          showError(`"${file.name}" is not an image.`);
          continue;
        }
        const compressed = await compressImage(file);
        if (compressed.size > MAX_BYTES) {
          showError(`"${file.name}" is over 8 MB — please use a smaller photo.`);
          continue;
        }
        photos.push(compressed);
        renderPhotos();
      }
      renderPhotos();
    };

    drop.addEventListener('click', () => fileInput.click());
    drop.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        fileInput.click();
      }
    });
    fileInput.addEventListener('change', () => {
      addFiles(fileInput.files);
      fileInput.value = '';
    });
    ['dragenter', 'dragover'].forEach((type) =>
      drop.addEventListener(type, (event) => {
        event.preventDefault();
        drop.classList.add('is-drag');
      })
    );
    ['dragleave', 'drop'].forEach((type) =>
      drop.addEventListener(type, (event) => {
        event.preventDefault();
        drop.classList.remove('is-drag');
      })
    );
    drop.addEventListener('drop', (event) => addFiles(event.dataTransfer.files));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      showError('');

      const name = form.elements.name.value.trim();
      const email = form.elements.email.value.trim();
      if (!name) return showError('Please enter your name.');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return showError('Please enter a valid email address.');
      }
      if (photos.length === 0) {
        return showError('Please add at least one photo of your space.');
      }
      if (!endpoint) {
        return showError('This form is not connected yet — please email us instead.');
      }

      const styles = readStyles();
      const data = new FormData();
      data.append('name', name);
      data.append('email', email);
      data.append('message', form.elements.message.value.trim());
      data.append('website', form.elements.website.value);
      data.append('styles', JSON.stringify(styles));
      photos.forEach((file) => data.append('photos', file, file.name));

      submitBtn.disabled = true;
      submitBtn.textContent = submitBtn.dataset.labelSending || 'Sending…';

      // Post with retries: on a weak connection (customer photos are MBs) the
      // upload can take minutes and the socket may drop, surfacing as a
      // TypeError "Failed to fetch". Retry those transient network failures a
      // couple of times before giving up. Server responses (even errors) are
      // NOT retried — those are answered, not dropped.
      const MAX_ATTEMPTS = 3;
      const postOnce = async () => {
        const response = await fetch(endpoint, { method: 'POST', body: data });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          const err = new Error(result.error || 'Something went wrong — please try again.');
          err.fromServer = true;
          throw err;
        }
        return result;
      };

      try {
        let lastError;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
          try {
            await postOnce();
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            if (error.fromServer || attempt === MAX_ATTEMPTS) break;
            submitBtn.textContent = `Retrying… (${attempt}/${MAX_ATTEMPTS - 1})`;
            await new Promise((r) => setTimeout(r, 1500 * attempt));
          }
        }
        if (lastError) throw lastError;

        // Picks are intentionally KEPT (not cleared) so the customer can try another set.
        pushEvent('style_preview_submit', {
          photos_count: photos.length,
          styles_count: styles.length,
          page_url: window.location.href,
        });
        form.hidden = true;
        if (successBox) {
          successBox.hidden = false;
          successBox.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      } catch (error) {
        // Friendly message for network drops; keep server-sent messages verbatim.
        showError(
          error.fromServer
            ? error.message
            : 'Couldn’t send — your connection looks unstable. Please try again on a stronger network.'
        );
        submitBtn.disabled = false;
        submitBtn.textContent = submitBtn.dataset.labelDefault || 'Send My Free Preview';
      }
    });
  };

  const initAll = (root) => {
    root.querySelectorAll('[data-fspf-root]').forEach(initForm);
  };

  initAll(document);
  // Theme editor re-renders sections; re-bind on load.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
